/**
 * Branding Context — holds the current tenant branding configuration.
 *
 * - Fetches from /api/v1/branding on mount (if auth token exists)
 * - Falls back to SYSTEM_DEFAULT_BRANDING if API fails or no token
 * - Exposes updateBranding() so the branding page can push changes immediately
 * - Never blocks rendering — shows default theme instantly, updates when API responds
 *
 * Wave 1c W1c-08 — Hot-reload across the running UI:
 *   - When updateBranding() is called locally (e.g., after a successful save in
 *     the branding editor), the new branding is broadcast on a
 *     BroadcastChannel('argusai-branding') so other open tabs of the same
 *     origin / same browser session re-apply without polling or refresh.
 *   - On mount, every BrandingProvider subscribes to that channel; messages
 *     scoped to a different tenant are ignored (the payload carries tenantId).
 *   - Favicon hot-reload + document.title sync are handled by ThemeProvider
 *     (which depends on `branding`), so just calling setBranding() is enough
 *     to flush the new effective branding through the entire UI.
 *   - Falls back gracefully when BroadcastChannel is unavailable (kiosk
 *     browsers, JSDom test envs); the originating tab still updates.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { apiClient } from '@/lib/api-client';
import { SYSTEM_DEFAULT_BRANDING } from '@/lib/branding-defaults';
import type { TenantBranding } from '@/lib/branding-defaults';

interface BrandingContextType {
  branding: TenantBranding;
  /**
   * Apply a new branding to this tab + broadcast to peers (same origin,
   * same browser session). Call this after a successful PUT /branding
   * to give the user instant feedback without a page refresh.
   */
  updateBranding: (settings: TenantBranding) => void;
  /**
   * Internal — apply a branding update received from a peer tab. Does
   * NOT re-broadcast (would cause an infinite ping-pong).
   */
  applyRemoteBranding: (settings: TenantBranding) => void;
  isLoaded: boolean;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

interface BrandingProviderProps {
  children: ReactNode;
}

/** Channel name shared across all open tabs of this origin. */
const BRANDING_CHANNEL = 'argusai-branding';

/**
 * Wire format for cross-tab branding messages. We carry tenantId so a tab
 * for tenant A doesn't apply tenant B's branding (e.g., switched accounts
 * in a private window). Source-of-truth is the receiving tab's auth state.
 */
interface BrandingBroadcastMessage {
  type: 'branding:updated';
  tenantId: string | null;
  effectiveBranding: TenantBranding;
}

/** Best-effort access to the current tenant id from the access token. */
function readTenantIdFromToken(): string | null {
  const token = localStorage.getItem('accessToken');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Standalone JWT carries `tenantId`; service JWT carries `org` (per
    // CLAUDE.md §12.2). Prefer tenantId; fall back to org.
    return (payload?.tenantId as string) ?? (payload?.org as string) ?? null;
  } catch {
    return null;
  }
}

export function BrandingProvider({ children }: BrandingProviderProps) {
  const [branding, setBranding] = useState<TenantBranding>(SYSTEM_DEFAULT_BRANDING);
  const [isLoaded, setIsLoaded] = useState(false);
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Fetch branding from API on mount — best-effort, never crash.
  // Skip the fetch entirely when the active token is a sysadmin token
  // (audience `argusai-admin`). Sysadmin context has no per-tenant
  // branding; the Surface B layout always uses the platform defaults.
  // Without this skip, the call 401s and the apiClient's refresh path
  // tries the tenant-only `/auth/refresh` endpoint, which also 401s,
  // ultimately bouncing the sysadmin to /login. (UI bug #1 caught by
  // the Playwright harness.)
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setIsLoaded(true);
      return;
    }

    // Decode JWT payload (no verification — the server validates).
    // `aud === 'argusai-admin'` → sysadmin context → don't fetch tenant
    // branding. Bare `try` because a malformed token shouldn't crash
    // the provider.
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload?.aud === 'argusai-admin') {
        setIsLoaded(true);
        return;
      }
    } catch {
      // Malformed token — fall through and let the API call fail
      // gracefully via the catch below.
    }

    apiClient
      .get<TenantBranding>('/branding')
      .then((data) => {
        if (data && typeof data === 'object' && 'light' in data && 'dark' in data) {
          setBranding(data);
        }
      })
      .catch(() => {
        // API unavailable — keep defaults, no crash
      })
      .finally(() => {
        setIsLoaded(true);
      });
  }, []);

  // ── Wave 1c W1c-08 — BroadcastChannel listener ──────────────────────
  //
  // Every BrandingProvider opens the same channel, then receives any
  // peer's `branding:updated` payload and applies it locally. The
  // `applyRemoteBranding` path bypasses the broadcast step to avoid a
  // ping-pong loop.
  useEffect(() => {
    // Feature-detect — kiosk browsers + JSDom skip cleanly.
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
      return;
    }

    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(BRANDING_CHANNEL);
      channelRef.current = channel;
    } catch {
      // BroadcastChannel constructor can throw in extremely locked-down
      // environments; treat as unavailable.
      return;
    }

    const handler = (event: MessageEvent<BrandingBroadcastMessage>) => {
      const msg = event.data;
      if (!msg || msg.type !== 'branding:updated') return;
      // Tenant-isolation: ignore if the message is for a different tenant
      // than the one this tab is logged in as. Sysadmin tokens read null;
      // we treat null as "match anything" so cross-window sysadmin tabs
      // also stay coherent with the platform default.
      const localTenantId = readTenantIdFromToken();
      if (localTenantId && msg.tenantId && msg.tenantId !== localTenantId) return;
      if (msg.effectiveBranding && typeof msg.effectiveBranding === 'object') {
        setBranding(msg.effectiveBranding);
      }
    };

    channel.addEventListener('message', handler);
    return () => {
      channel?.removeEventListener('message', handler);
      channel?.close();
      channelRef.current = null;
    };
  }, []);

  /**
   * Public API — called by the editor after a successful save. Applies
   * locally + broadcasts to peer tabs.
   */
  const updateBranding = useCallback((settings: TenantBranding) => {
    setBranding(settings);

    // Broadcast (best-effort).
    const channel = channelRef.current;
    if (channel) {
      const msg: BrandingBroadcastMessage = {
        type: 'branding:updated',
        tenantId: readTenantIdFromToken(),
        effectiveBranding: settings,
      };
      try {
        channel.postMessage(msg);
      } catch {
        // postMessage can throw on detached channels; a refresh would
        // recover, but never let this crash the app.
      }
    }
  }, []);

  /**
   * Used by tests + future programmatic flows that need to apply a
   * remote-style update without re-broadcasting.
   */
  const applyRemoteBranding = useCallback((settings: TenantBranding) => {
    setBranding(settings);
  }, []);

  return (
    <BrandingContext.Provider
      value={{ branding, updateBranding, applyRemoteBranding, isLoaded }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding(): BrandingContextType {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}

// Re-export types for convenience
export type { TenantBranding, BrandingContextType };
